import { Inject, Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs";
import { GetPartnersSheetProcessingProgressInteractor } from "../../../../features/brand-campaign-partners/domain/interactors/get-partners-sheet-processing-progress.interactor";
import { GetPartnersSheetStateInteractor } from "../../../../features/brand-campaign-partners/domain/interactors/get-partners-sheet-state.interactor";
import { PartnersSheetStatus } from "../../../../features/brand-campaign-partners/domain/models/partners-sheet-status.enum";
import {
  IntervalRunner,
  IntervalRunnerFactory,
  INTERVAL_RUNNER_FACTORY,
} from "../../../../features/shared/interval-runner/interval-runner";

export interface IPartnersSheetProcessingProgressService {
  progress: number | undefined;
  progress$: Observable<number | undefined>;
  check(): Promise<void>;
  clearInterval(): void;
  start(currentValue: number | undefined): void;
  stop(): void;
}

@Injectable()
export class PartnersSheetProcessingProgressService
  implements IPartnersSheetProcessingProgressService
{
  private readonly progressSubject$ = new BehaviorSubject<number | undefined>(
    undefined,
  );
  private readonly campaignId: number;
  private processingInterval?: IntervalRunner;

  constructor(
    route: ActivatedRoute,
    private readonly getPartnersSheetProcessingProgress: GetPartnersSheetProcessingProgressInteractor,
    private readonly getPartnersSheetState: GetPartnersSheetStateInteractor,
    @Inject(INTERVAL_RUNNER_FACTORY)
    private readonly createIntervalRunner: IntervalRunnerFactory,
  ) {
    this.campaignId = route.snapshot.params.campaignId;
  }

  public get progress(): number | undefined {
    return this.progressSubject$.value;
  }

  public get progress$(): Observable<number | undefined> {
    return this.progressSubject$.asObservable();
  }

  public async check(): Promise<void> {
    this.clearInterval();

    const partnerSheetState = await this.getPartnersSheetState.execute(
      this.campaignId,
    );

    switch (partnerSheetState.status) {
      case PartnersSheetStatus.Processing:
        this.start();
        break;

      case PartnersSheetStatus.Matching:
        this.progressSubject$.next(100.0);
        break;

      default:
        this.stop();
    }
  }

  public clearInterval(): void {
    this.processingInterval?.clear();
  }

  public start(currentValue = 0): void {
    this.clearInterval();
    this.progressSubject$.next(currentValue);

    this.processingInterval = this.createIntervalRunner(1000, async () => {
      try {
        const progress = await this.getPartnersSheetProcessingProgress.execute(
          this.campaignId,
        );

        this.progressSubject$.next(progress);

        if (progress >= 100.0) {
          this.clearInterval();
        }
      } catch {}
    });
  }

  public stop(): void {
    this.clearInterval();
    this.progressSubject$.next(undefined);
  }
}
